import { ToastrService, ToastContainerDirective } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  HostListener,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { SortDirection } from '@angular/material/sort';

import { ISnListQueryParameters } from '@lib/api';
import { FsSavedSearch, SnDetectionFrame } from '@lib/api/entities';
import { SnCamerasAPI, SnSavedSearchAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { SnPlayerService } from 'libs/player/common/src/lib/services/player.service';

type FsQuickSearch = {
  appliance_serial_numbers__size: number;
  camera_id__in: string;
  cameras__size: number;
  confidence__gte: number;
  label__in: string;
  timestamp__gte: number;
  timestamp__lte: number;
  timestamp__hour__gte__offset: string;
  timestamp__hour__lte__offset: string;
  events__label__in: string;
};

type FsSearchType = FsSavedSearch | FsQuickSearch;

@Component({
  selector: 'fs-sentry-results-component',
  templateUrl: './sentry-results-component.component.html',
  styleUrls: ['./sentry-results-component.component.scss'],
})
export class SentryResultsComponentComponent implements OnInit, OnDestroy {
  public scrollTop: number = 0;

  @Input()
  set inputValue(value: any) {
    if (value) {
      this.data = value;
    }
  }

  @Input() public pursuitMode: boolean;

  @ViewChild(ToastContainerDirective, { static: true })
  public toastContainer: ToastContainerDirective;

  public data: FsSearchType;
  public isLoading = false;
  public sort = 'createdTimestamp';
  public count: number;
  public sortDirection: SortDirection = 'desc';
  public aiResults: SnDetectionFrame[] = [];
  public totalPages: number;
  public allowAi: boolean;

  private readonly _destroy$ = new Subject<void>();

  private _params: ISnListQueryParameters = {
    page: 1,
    pageSize: 100,
  };

  constructor(
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _playerService: SnPlayerService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _toastrService: ToastrService,
    private readonly _elementRef: ElementRef,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _sentrySearchApi: SnSavedSearchAPI,
  ) {
    this.allowAi = this._currentOrganization.plan.allowAi;
  }

  public ngOnInit(): void {
    this.isQuickSearch(this.data)
      ? this._fetchAiCameraSearch(this.data)
      : this._fetchSentryResults();

    this._playerService.fullscreen$.pipe(takeUntil(this._destroy$)).subscribe((event) => {
      if (event === 'close') {
        this.setScrollPosition();
      }
    });
    this._toastrService.overlayContainer = this.toastContainer;
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public isQuickSearch(search: FsSearchType): search is FsQuickSearch {
    return 'camera_id__in' in search;
  }

  public setScrollPosition(): void {
    setTimeout(() => {
      const div = this._elementRef.nativeElement.querySelector('sn-drawer-content');
      div.scrollTop = this.scrollTop;
    }, 250);
  }

  @HostListener('scroll', ['$event'])
  public onScroll(event: any): void {
    this.scrollTop = event.target.scrollTop;
    if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight - 1) {
      this._loadOptions();
    }
  }

  public trackSentrySearch(index: number, item: SnDetectionFrame): any {
    return item;
  }

  private _loadOptions(): void {
    if (this._params.page === this.totalPages) {
      return;
    }
    this._params = {
      ...this._params,
      page: this._params.page + 1,
    };
    this._fetchAiCameraSearch(this._params);
  }

  private _fetchAiCameraSearch(params: any): void {
    this.isLoading = true;
    this._params = {
      ...params,
      ...this._params,
    };
    this._camerasAPI
      .getAiList({
        ...params,
      })
      .pipe(
        tap((response) => {
          this.count = response.count;
          this.totalPages = response.totalPages;
        }),
        map((response) => response.results),
        takeUntil(this._destroy$),
      )
      .subscribe({
        next: (response: SnDetectionFrame[]) => {
          if (this.aiResults.length > 0) {
            const newResults: SnDetectionFrame[] = [];
            newResults.push(...response);
            this.aiResults.push(
              ...newResults.filter((result) => !this.aiResults.some((ai) => ai.id === result.id)),
            );
          } else {
            this.aiResults.push(...response);
          }
          this._cd.detectChanges();
        },
        error: () => {
          this.isLoading = false;
          this._cd.detectChanges();
        },
        complete: () => {
          this.isLoading = false;
          this._cd.detectChanges();
        },
      });
  }

  private _fetchSentryResults(): void {
    this._sentrySearchApi
      .getSearchById((this.data as FsSavedSearch).id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((search: any) => {
        this._params = {
          ...this._params,
          ...search.queryParams,
        };
        this._fetchAiCameraSearch(this._params);

        this._cd.detectChanges();
      });
  }
}
